import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, FileCheck2, PenLine } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  CLIENT_INV_STATUS, DATA_QUALITY, fmtDate, fmtDateShort, fmtDateTime, fmtMoney, fmtMoneyShort, GATE_STATUS, INVOICE_STATUS,
  LIFECYCLE_PHASES, PHASE_LABELS, PO_STATUS, PROJECT_HEALTH, PROJECT_STATUS,
} from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, FactStrip, PageHeader, ReasonsList, ToneChip } from "../../components/common";
import { ApprovalCard } from "../../components/panels";
import { FinActions, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "fases", label: "Fases y gates" },
  { id: "relaciones", label: "Contratos y compras" },
  { id: "actividad", label: "Actividad" },
];

function PlanningShortcut({ pid }) {
  return (
    <>
      <Link to={`/proyectos/planificacion?project=${pid}`} data-testid="planning-shortcut"
        className="ts-chip ts-chip-info inline-flex items-center gap-1 hover:ring-1 hover:ring-blue-400">
        Planificación y control de avance →
      </Link>
      <Link to={`/proyectos/economia?project=${pid}`} data-testid="economics-shortcut"
        className="ts-chip ts-chip-info inline-flex items-center gap-1 hover:ring-1 hover:ring-blue-400">
        Control económico y certificaciones →
      </Link>
    </>
  );
}

const ROLE_LABELS = {
  sponsor_id: "Sponsor", director_id: "Director", pm_id: "Project Manager", cost_manager_id: "Cost Manager",
  planner_id: "Planner", technical_manager_id: "Technical Manager", quality_manager_id: "Quality Manager",
  permits_owner_id: "Permits Owner", hs_reviewer_id: "H&S Reviewer",
};

function CharterDialog({ project, open, onClose, onDone }) {
  const [form, setForm] = useState({ ...(project.charter || {}) });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/proyectos/${project.id}/charter`, form);
      toast.success("Charter actualizado y registrado");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="charter-dialog">
        <DialogHeader><DialogTitle>Charter del proyecto</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[["objective", "Objetivo"], ["scope", "Alcance inicial"], ["success_criteria", "Criterios de éxito"], ["hypotheses", "Hipótesis"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`charter-${k}`} /></div>
          ))}
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="charter-submit">{busy ? "Guardando…" : "Guardar charter"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReqDialog({ project, gate, req, open, onClose, onDone }) {
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/proyectos/${project.id}/gates/${gate.id}/action`, { action: "marcar_requisito", req_code: req.code, evidence });
      toast.success("Requisito marcado con evidencia");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="req-dialog">
        <DialogHeader><DialogTitle>{req?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Un requisito de puerta exige evidencia trazable (documento, acta, referencia), no solo una marca.</p>
          <div><Label>Evidencia / referencia {req?.kind === "evidence" ? "*" : ""}</Label><Textarea value={evidence} onChange={(e) => setEvidence(e.target.value)} data-testid="req-evidence" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || (req?.kind === "evidence" && !evidence.trim())} data-testid="req-submit">
            {busy ? "Registrando…" : "Marcar requisito"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GateCard({ project, gate, actions, onAct, onMarkReq }) {
  const done = gate.requirements.filter((r) => r.done).length;
  return (
    <div className="ts-card p-5" data-testid={`gate-${gate.gate_key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-display text-sm font-semibold">{gate.label}</p>
          <Chip map={GATE_STATUS} value={gate.status} testid={`gate-status-${gate.gate_key}`} />
          <span className="font-mono text-xs text-slate-400">{done}/{gate.requirements.length} requisitos</span>
        </div>
        {gate.decided_by && <p className="text-xs text-slate-400">Decidida {fmtDate(gate.decided_at)}</p>}
      </div>
      {gate.decision_comment && <p className="mt-1 text-xs text-slate-500">Decisión: {gate.decision_comment}</p>}
      <div className="mt-3 space-y-1.5">
        {gate.requirements.map((r) => (
          <div key={r.code} className="flex items-center justify-between gap-2 text-sm" data-testid={`gate-req-${gate.gate_key}-${r.code}`}>
            <span className={`flex items-center gap-2 ${r.done ? "text-slate-700" : "text-slate-500"}`}>
              <span className={`flex h-4 w-4 items-center justify-center rounded-full ${r.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                {r.done && <Check className="h-3 w-3" />}
              </span>
              {r.label}
              {r.auto && <ToneChip label="auto" tone="muted" />}
            </span>
            {!r.done && ["en_preparacion", "cambios_requeridos"].includes(gate.status) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onMarkReq(r)} data-testid={`req-mark-${gate.gate_key}-${r.code}`}>
                <PenLine className="mr-1 h-3 w-3" /> Marcar
              </Button>
            )}
          </div>
        ))}
      </div>
      {actions?.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <FinActions actions={actions} onAct={(action, payload) => onAct(gate, action, payload)} testid={`gate-action-${gate.gate_key}`} />
        </div>
      )}
    </div>
  );
}

export default function ProyectoDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "resumen";
  const qc = useQueryClient();
  const { map } = useUsers();
  const { user } = useAuth();
  const canWrite = (user?.roles || []).some((r) => r !== "VIEWER");
  const [charterOpen, setCharterOpen] = useState(false);
  const [reqDialog, setReqDialog] = useState(null); // {gate, req}
  const { data, error, refetch } = useQuery({
    queryKey: ["proyecto", id],
    queryFn: async () => (await api.get(`/proyectos/${id}`)).data,
  });
  const { data: activity } = useQuery({
    queryKey: ["proyecto-activity", id],
    queryFn: async () => (await api.get("/activity", { params: { entity_id: id, limit: 200 } })).data,
    enabled: tab === "actividad",
  });
  if (error) {
    return (
      <div className="ts-card mx-auto mt-16 max-w-md p-8 text-center" data-testid="project-no-access">
        <p className="font-display text-lg font-semibold text-slate-900">Sin acceso a este proyecto</p>
        <p className="mt-2 text-sm text-slate-500">Es confidencial o restringido y no perteneces a su equipo. Pide acceso al Project Manager o al Sponsor.</p>
        <Link to="/proyectos/lista" className="mt-4 inline-block text-sm font-medium text-blue-700 hover:underline">← Volver a proyectos</Link>
      </div>
    );
  }
  if (!data) return null;
  const { project: p, actions, gates, gate_actions, data_quality, relations, tasks, approvals } = data;
  const phaseIdx = LIFECYCLE_PHASES.indexOf(p.lifecycle_phase);

  const act = async (action, payload) => {
    await api.post(`/proyectos/${p.id}/action`, { action, ...payload });
    toast.success("Acción ejecutada y registrada");
    refetch();
    qc.invalidateQueries({ queryKey: ["proyectos"] });
    qc.invalidateQueries({ queryKey: ["proj-dashboard"] });
  };
  const gateAct = async (gate, action, payload) => {
    await api.post(`/proyectos/${p.id}/gates/${gate.id}/action`, { action, ...payload });
    toast.success("Acción de puerta registrada");
    refetch();
  };

  return (
    <div data-testid="proyecto-detail">
      <Link to="/proyectos/lista" className="mb-2 inline-block text-xs font-medium text-blue-700 hover:underline" data-testid="project-back">← Proyectos</Link>
      <Link to={`/resumen/project/${p.id}`} className="mb-2 ml-4 inline-block text-xs font-medium text-blue-700 hover:underline" data-testid="project-summary-link">Executive summary →</Link>
      <PageHeader title={p.name} subtitle={`${p.project_code} · ${p.municipality || "—"}, ${p.region || "—"} · Cliente: ${p.client || "—"} · Control: ${fmtDate(p.control_date)}`} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Chip map={PROJECT_STATUS} value={p.status} testid="project-detail-status" />
        <span className="ts-chip ts-chip-info" data-testid="project-detail-phase">{PHASE_LABELS[p.lifecycle_phase]}</span>
        <Chip map={PROJECT_HEALTH} value={p.health_status} testid="project-detail-health" />
        <Chip map={DATA_QUALITY} value={data_quality.status} testid="project-detail-dq" />
        {p.blocked_reason && <ToneChip label={p.blocked_reason} tone="danger" testid="project-blocked-chip" />}
        {p.economics_restricted && <ToneChip label="Economía restringida (contratista)" tone="muted" testid="project-econ-restricted" />}
        <PlanningShortcut pid={p.id} />
      </div>
      <FinActions actions={actions} onAct={act} testid="project-action" />

      {(() => {
        const nextGate = gates.find((g) => g.status !== "aprobada");
        const attention = [
          p.blocked_reason && { text: `Bloqueado: ${p.blocked_reason}`, tone: "danger", to: "?tab=fases" },
          ...tasks.filter((t) => t.status === "bloqueada").slice(0, 2).map((t) => ({ text: `Tarea bloqueada: ${t.title}`, tone: "danger", to: "?tab=relaciones" })),
          ...approvals.filter((x) => x.status === "solicitada").slice(0, 2).map((x) => ({ text: `Pendiente de aprobación: ${x.subject}`, tone: "ai", to: "/aprobaciones?vista=mias" })),
          ...data_quality.checks.filter((c) => !c.ok).slice(0, 2).map((c) => ({ text: `Calidad del dato: ${c.detail}`, tone: "warning", to: "?tab=resumen" })),
        ].filter(Boolean).slice(0, 4);
        return (
          <>
            <div className="ts-hero-neutral mb-4 rounded-sm px-6 py-5" data-testid="project-hero">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <p className="font-display text-xl font-bold tracking-wide text-white">{p.name}</p>
                <span className="text-xs uppercase tracking-widest text-slate-400">{p.municipality} · {p.region}</span>
                <span className="ts-chip border-white/30 bg-white/10 text-white" data-testid="hero-phase">{PHASE_LABELS[p.lifecycle_phase]}</span>
                <span className="ts-chip border-white/30 bg-white/10 text-white" data-testid="hero-status">{PROJECT_STATUS[p.status]?.label || p.status}</span>
              </div>
            </div>
            <div className="ts-card mb-4 p-5" data-testid="project-exec-header">
              <FactStrip testid="project-fact-strip" facts={[
                { label: "Localización", value: [p.municipality, p.region].filter(Boolean).join(", ") },
                { label: "Fase", value: PHASE_LABELS[p.lifecycle_phase] },
                { label: "Presupuesto", value: fmtMoneyShort(p.current_budget), mono: true },
                { label: "Forecast", value: fmtMoneyShort(p.forecast_final_cost), mono: true, tone: p.variance < 0 ? "danger" : undefined },
                { label: "Desviación", value: fmtMoneyShort(p.variance), mono: true, tone: p.variance < 0 ? "danger" : "success" },
                { label: "Avance físico", value: p.physical_progress != null ? `${p.physical_progress}%` : null, mono: true },
                { label: "Próxima puerta", value: nextGate?.label },
                { label: "Fin previsto", value: fmtDateShort(p.current_forecast_completion) },
              ]} />
              {attention.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid="project-attention">
                  <p className="ts-caption mb-1">Necesita atención</p>
                  <ul className="space-y-1">
                    {attention.map((it, i) => (
                      <li key={i}>
                        <Link to={it.to} className="flex items-center gap-2 text-sm text-slate-700 hover:text-blue-700" data-testid={`project-attention-${i}`}>
                          <ToneChip label={it.tone === "danger" ? "crítico" : it.tone === "ai" ? "decisión" : "aviso"} tone={it.tone === "ai" ? "ai" : it.tone} />
                          <span className="truncate">{it.text}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        );
      })()}

      <div className="mb-4 mt-6 flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setParams({ tab: t.id })} data-testid={`tab-${t.id}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === t.id ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "resumen" && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <div className="ts-card p-5" data-testid="project-key-facts">
              <div className="grid gap-4 sm:grid-cols-3">
                <div><p className="ts-caption">Presupuesto actual</p><Money value={p.current_budget} className="text-xl font-bold text-slate-900" /></div>
                <div><p className="ts-caption">Forecast final</p><Money value={p.forecast_final_cost} className={`text-xl font-bold ${(p.variance ?? 0) < 0 ? "text-red-600" : "text-slate-900"}`} /></div>
                <div>
                  <p className="ts-caption">Variación (ahorro + / sobrecoste −)</p>
                  <Money value={p.variance} className={`text-xl font-bold ${(p.variance ?? 0) < 0 ? "text-red-600" : "text-emerald-700"}`} />
                </div>
                <div><p className="ts-caption">Avance físico</p><p className="font-mono text-xl font-bold">{p.physical_progress}%</p></div>
                <div><p className="ts-caption">Avance financiero</p><p className="font-mono text-xl font-bold">{p.financial_progress != null ? `${p.financial_progress}%` : "—"}</p></div>
                <div><p className="ts-caption">Fin previsto</p>
                  <p className={`text-sm font-semibold ${p.current_forecast_completion > p.baseline_completion ? "text-red-600" : "text-slate-900"}`}>
                    {fmtDate(p.current_forecast_completion)}
                  </p>
                  <p className="text-xs text-slate-400">baseline: {fmtDate(p.baseline_completion)}</p>
                </div>
              </div>
              <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500 sm:grid-cols-4">
                <span>Comprometido: <Money value={p.committed} /></span>
                <span>Facturado: <Money value={p.invoiced} /></span>
                <span>Pagado: <Money value={p.paid} /></span>
                <span>Certificado: <Money value={p.certified} /></span>
              </div>
            </div>

            <div className="ts-card p-5" data-testid="project-charter">
              <div className="flex items-center justify-between">
                <p className="font-display text-sm font-semibold">Charter</p>
                {canWrite && <Button size="sm" variant="outline" onClick={() => setCharterOpen(true)} data-testid="charter-edit-button"><PenLine className="mr-1 h-3.5 w-3.5" /> Editar</Button>}
              </div>
              <div className="mt-2 space-y-2 text-sm">
                <p><span className="ts-caption mr-2">Objetivo</span>{p.charter?.objective || <span className="text-amber-600">pendiente</span>}</p>
                <p><span className="ts-caption mr-2">Alcance</span>{p.charter?.scope || <span className="text-amber-600">pendiente</span>}</p>
                {p.charter?.success_criteria && <p><span className="ts-caption mr-2">Éxito</span>{p.charter.success_criteria}</p>}
                {p.charter?.hypotheses && <p><span className="ts-caption mr-2">Hipótesis</span>{p.charter.hypotheses}</p>}
              </div>
            </div>

            <div className="ts-card p-5" data-testid="project-dq">
              <p className="mb-2 font-display text-sm font-semibold">Calidad del dato</p>
              <div className="space-y-1.5">
                {data_quality.checks.map((c) => (
                  <div key={c.check} className="flex items-center justify-between text-sm" data-testid={`dq-${c.check}`}>
                    <span className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${c.ok ? "bg-emerald-500" : "bg-red-500"}`} />
                      {c.detail}
                    </span>
                    {c.owner_id && <span className="text-xs text-slate-400">{userName(map, c.owner_id)}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="ts-card p-5" data-testid="project-team">
              <p className="mb-2 font-display text-sm font-semibold">Equipo</p>
              <div className="space-y-1.5">
                {Object.entries(ROLE_LABELS).map(([f, label]) => (
                  <div key={f} className="flex items-center justify-between text-sm" data-testid={`team-${f}`}>
                    <span className="text-slate-500">{label}</span>
                    <span className={`font-medium ${p[f] ? "text-slate-800" : "text-amber-600"}`}>{p[f] ? userName(map, p[f]) : "sin asignar"}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3" data-testid="project-approvals">
              {approvals.map((a) => <ApprovalCard key={a.id} appr={a} me={null} onChanged={refetch} />)}
            </div>
          </div>
        </div>
      )}

      {tab === "fases" && (
        <div className="space-y-4">
          <div className="ts-card overflow-x-auto p-5" data-testid="phase-stepper">
            <div className="flex min-w-max items-center gap-1">
              {LIFECYCLE_PHASES.map((ph, i) => (
                <React.Fragment key={ph}>
                  <div className={`rounded-sm px-2.5 py-1.5 text-xs font-medium ${i < phaseIdx ? "bg-emerald-100 text-emerald-800" : i === phaseIdx ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-400"}`} data-testid={`phase-step-${ph}`}>
                    {PHASE_LABELS[ph]}
                  </div>
                  {i < LIFECYCLE_PHASES.length - 1 && <span className="text-slate-300">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">Las fases solo avanzan cuando su puerta de decisión se aprueba. Las puertas aprobadas son inmutables; la siguiente se abre automáticamente.</p>
          <div className="grid gap-4 lg:grid-cols-2">
            {gates.map((g) => (
              <GateCard key={g.id} project={p} gate={g} actions={gate_actions[g.id]} onAct={gateAct}
                onMarkReq={(req) => setReqDialog({ gate: g, req })} />
            ))}
          </div>
        </div>
      )}

      {tab === "relaciones" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="ts-card overflow-hidden" data-testid="rel-contracts">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Contratos ({relations.contracts.length})</p>
            <Table>
              <TableHeader><TableRow><TableHead>Contrato</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Importe</TableHead></TableRow></TableHeader>
              <TableBody>
                {relations.contracts.length === 0 && <TableRow><TableCell colSpan={3} className="py-5 text-center text-sm text-slate-500">Sin contratos vinculados.</TableCell></TableRow>}
                {relations.contracts.map((c) => (
                  <TableRow key={c.id} data-testid={`rel-contract-${c.id}`}>
                    <TableCell><Link to={`/legal/contratos/${c.id}`} className="text-sm text-blue-700 hover:underline">{c.title}</Link><p className="font-mono text-xs text-slate-400">{c.contract_number}</p></TableCell>
                    <TableCell className="text-xs">{c.status}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMoney(c.total_value ?? c.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="ts-card overflow-hidden" data-testid="rel-pos">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Órdenes de compra ({relations.purchase_orders.length})</p>
            <Table>
              <TableHeader><TableRow><TableHead>Orden</TableHead><TableHead>Proveedor</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {relations.purchase_orders.length === 0 && <TableRow><TableCell colSpan={4} className="py-5 text-center text-sm text-slate-500">Sin órdenes vinculadas.</TableCell></TableRow>}
                {relations.purchase_orders.map((po) => (
                  <TableRow key={po.id} data-testid={`rel-po-${po.id}`}>
                    <TableCell><Link to={`/finanzas/ordenes/${po.id}`} className="font-mono text-xs text-blue-700 hover:underline">{po.po_number}</Link></TableCell>
                    <TableCell className="text-xs">{po.supplier_name}</TableCell>
                    <TableCell><Chip map={PO_STATUS} value={po.status} /></TableCell>
                    <TableCell className="text-right"><Money value={po.total} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="ts-card overflow-hidden" data-testid="rel-invoices">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Facturas ({relations.invoices.length})</p>
            <Table>
              <TableHeader><TableRow><TableHead>Factura</TableHead><TableHead>Proveedor</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {relations.invoices.length === 0 && <TableRow><TableCell colSpan={4} className="py-5 text-center text-sm text-slate-500">Sin facturas vinculadas.</TableCell></TableRow>}
                {relations.invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`rel-inv-${inv.id}`}>
                    <TableCell><Link to={`/finanzas/facturas/${inv.id}`} className="font-mono text-xs text-blue-700 hover:underline">{inv.invoice_number}</Link></TableCell>
                    <TableCell className="text-xs">{inv.supplier_name}</TableCell>
                    <TableCell><Chip map={INVOICE_STATUS} value={inv.status} /></TableCell>
                    <TableCell className="text-right"><Money value={inv.total} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="ts-card overflow-hidden" data-testid="rel-tasks">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Tareas del proyecto ({tasks.length})</p>
            <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
              {tasks.length === 0 && <p className="px-5 py-5 text-center text-sm text-slate-500">Sin tareas.</p>}
              {tasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 px-5 py-2.5" data-testid={`rel-task-${t.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm">{t.title}</p>
                    <p className="text-xs text-slate-400">{userName(map, t.assignee_id)} · vence {fmtDate(t.due_date)}</p>
                  </div>
                  <ToneChip label={t.status} tone={t.status === "pendiente" ? "warning" : t.status === "completada" ? "success" : "info"} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "actividad" && (
        <div className="ts-card p-5" data-testid="project-activity">
          <p className="mb-3 font-display text-sm font-semibold">Actividad del proyecto ({activity?.length ?? "…"})</p>
          <div className="space-y-2">
            {(activity || []).map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 border-b border-slate-50 pb-2 text-sm" data-testid={`activity-${a.id}`}>
                <div>
                  <p className="text-slate-800">{a.detail}</p>
                  <p className="text-xs text-slate-400">{userName(map, a.actor_id)} · {a.action}{a.rule ? ` · regla: ${a.rule}` : ""}</p>
                </div>
                <span className="whitespace-nowrap text-xs text-slate-400">{fmtDateTime(a.timestamp)}</span>
              </div>
            ))}
            {activity && activity.length === 0 && <p className="text-sm text-slate-500">Sin actividad registrada.</p>}
          </div>
        </div>
      )}

      <CharterDialog project={p} open={charterOpen} onClose={() => setCharterOpen(false)} onDone={() => { setCharterOpen(false); refetch(); }} />
      {reqDialog && <ReqDialog project={p} gate={reqDialog.gate} req={reqDialog.req} open onClose={() => setReqDialog(null)} onDone={() => { setReqDialog(null); refetch(); }} />}
    </div>
  );
}
